/* global Image */
/**
 * The kitchen on paper.
 *
 * One PDF a person can send to a partner, print for the maker, or keep: the picture they
 * were looking at, the plan from above with the walls measured, what it is made of, what it
 * might cost, every cabinet with its size, and what the designer's review said. Nothing here
 * that is not already on screen -- it is the screen, in an envelope.
 *
 * jsPDF, drawn by hand: a table is a loop of lines and text, and a library that lays tables
 * out for you is another 200KB to say the same thing.
 */
import { jsPDF } from 'jspdf'
import { LAYER, toPaper } from './drawing.js'

const A4 = { w: 210, h: 297 }
const M = 16
const INK = [28, 25, 23]

/**
 * Writing on a jsPDF document, in millimetres.
 *
 * Lifted out of buildReport so the drawing sheets can write with the same hand. jsPDF is
 * stateful -- setFont, setFontSize, setTextColor all persist -- so every call here sets
 * everything it needs rather than assuming what the last one left behind, which is the bug
 * this shape of helper exists to prevent.
 */
export function pen(doc) {
  const text = (s, x, yy, size = 10, colour = INK, style = 'normal', align = 'left') => {
    doc.setFont('helvetica', style); doc.setFontSize(size); doc.setTextColor(...colour)
    doc.text(String(s), x, yy, { align })
  }
  const wrap = (s, x, yy, width, size = 10, colour = INK) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(size); doc.setTextColor(...colour)
    const lines = doc.splitTextToSize(String(s), width)
    doc.text(lines, x, yy)
    return lines.length * size * 0.42
  }
  return { text, wrap }
}

export async function buildReport({ plan, maker, sched, estimate, shot, planSvg: svg, findings = [] }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const ink = [28, 25, 23], mute = [120, 113, 108], line = [220, 214, 206]
  let y = M

  const { text, wrap } = pen(doc)
  const rule = (yy) => { doc.setDrawColor(...line); doc.setLineWidth(0.25); doc.line(M, yy, A4.w - M, yy) }
  const page = (need) => { if (y + need > A4.h - M) { doc.addPage(); y = M } }

  // ── page one: the kitchen ────────────────────────────────────────────────────────
  text(plan.name || 'Your kitchen', M, y + 6, 20, ink, 'bold')
  text(`${maker.name} · ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`, M, y + 12, 9.5, mute)
  y += 18

  if (shot) {
    // Fitted into the frame, never squashed to it: a tall phone still gets centred narrow.
    const img = await measure(shot)
    const box = { w: A4.w - M * 2, h: 108 }
    const k = Math.min(box.w / img.w, box.h / img.h)
    const w = img.w * k, h = img.h * k
    doc.addImage(shot, 'JPEG', M + (box.w - w) / 2, y, w, h, undefined, 'FAST')
    y += h + 5
  }

  // Plan and spec, side by side.
  const colW = (A4.w - M * 2 - 6) / 2
  const top = y
  if (svg) {
    try {
      const png = await svgToPng(svg, 1000)
      const h = colW * (png.h / png.w)
      doc.addImage(png.dataUrl, 'PNG', M, y, colW, h, undefined, 'FAST')
      y += h
    } catch { /* no plan picture; the words still say it */ }
  }
  let sy = top + 4
  const spec = [
    ['Cabinets', `${sched.counts.cabinets}${sched.counts.fillers ? `, ${sched.counts.fillers} filler${sched.counts.fillers > 1 ? 's' : ''}` : ''}`],
    ['Doors', sched.finishes.doors],
    ['Benchtop', `${sched.finishes.benchtop}, ${sched.benchtopMetres}m`],
    ['Splashback', sched.finishes.splashback],
    ['Handles', sched.finishes.handles],
    ...(estimate ? [['Estimate', `${estimate.text} incl. installation`]] : []),
  ]
  for (const [k, v] of spec) {
    text(k, M + colW + 6, sy, 8.5, mute)
    sy += 4
    sy += wrap(v, M + colW + 6, sy, colW - 2, 10.5, ink) + 2.5
  }
  y = Math.max(y, sy) + 4

  if (estimate) {
    page(8)
    wrap(`The estimate is a band from ${maker.name}'s price list for this design, including installation. `
      + 'They will price it properly from a site measure; nothing here is a quote.', M, y, A4.w - M * 2, 8.5, mute)
    y += 10
  }

  if (findings.length) {
    page(14)
    text('What a designer would say', M, y + 4, 12, ink, 'bold'); y += 9
    for (const f of findings) {
      page(14)
      text(f.level === 'fault' ? 'MUST FIX' : 'WORTH KNOWING', M, y, 7.5, f.level === 'fault' ? [138, 62, 30] : mute, 'bold')
      y += 3.8
      y += wrap(f.text, M, y, A4.w - M * 2, 9.5, ink) + 3
    }
  }

  // ── page two: the schedule ───────────────────────────────────────────────────────
  doc.addPage(); y = M
  text('Cabinet schedule', M, y + 6, 16, ink, 'bold'); y += 10
  text(`${sched.counts.cabinets} cabinets · ${sched.finishes.doors} · ${sched.finishes.handles}`, M, y, 9, mute); y += 6
  const cols = [
    { k: 'code', h: 'Code', w: 20 }, { k: 'name', h: 'Cabinet', w: 52 }, { k: 'run', h: 'Run', w: 16 },
    { k: 'w', h: 'W', w: 14 }, { k: 'h', h: 'H', w: 14 }, { k: 'd', h: 'D', w: 14 },
    { k: 'stretched', h: 'Note', w: A4.w - M * 2 - 130 },
  ]
  const header = () => {
    let x = M
    doc.setFillColor(245, 241, 235)
    doc.rect(M, y, A4.w - M * 2, 7, 'F')
    for (const c of cols) { text(c.h, x + 1.5, y + 4.8, 8, mute, 'bold'); x += c.w }
    y += 7
  }
  header()
  for (const l of sched.lines) {
    if (y + 7 > A4.h - M) { doc.addPage(); y = M; header() }
    let x = M
    for (const c of cols) {
      const v = l[c.k] ?? ''
      text(v === null ? '' : v, x + 1.5, y + 4.8, 8.5, c.k === 'stretched' ? mute : ink)
      x += c.w
    }
    rule(y + 7)
    y += 7
  }
  y += 6
  page(16)
  wrap('Sizes are what was drawn, in millimetres, width × height × depth. A cabinet marked "made at" is not a catalogue '
    + 'size: it was stretched to make the run add up, and it is why the run does. Everything is checked on site before it is made.',
  M, y, A4.w - M * 2, 8.5, mute)

  const n = doc.getNumberOfPages()
  for (let i = 1; i <= n; i++) {
    doc.setPage(i)
    text(`${maker.name} · page ${i} of ${n}`, A4.w - M, A4.h - 8, 7.5, mute, 'normal', 'right')
  }
  return doc.output('blob')
}

function measure(dataUrl) {
  return new Promise((resolve) => {
    const im = new Image()
    im.onload = () => resolve({ w: im.naturalWidth || 16, h: im.naturalHeight || 10 })
    im.onerror = () => resolve({ w: 16, h: 10 })
    im.src = dataUrl
  })
}

/** An SVG string to a PNG data URL, at `width` px, on a paper background. */
export function svgToPng(svg, width = 1000, background = '#F5F1EB') {
  return new Promise((resolve, reject) => {
    const src = svg.includes('xmlns=') ? svg : svg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
    const m = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(src) ?? /width="([\d.]+)"[^>]*height="([\d.]+)"/.exec(src)
    const vw = m ? +m[1] : 300, vh = m ? +m[2] : 220
    const im = new Image()
    const url = URL.createObjectURL(new Blob([src], { type: 'image/svg+xml' }))
    im.onload = () => {
      const c = document.createElement('canvas')
      c.width = width; c.height = Math.round(width * vh / vw)
      const g = c.getContext('2d')
      g.fillStyle = background; g.fillRect(0, 0, c.width, c.height)
      g.drawImage(im, 0, 0, c.width, c.height)
      URL.revokeObjectURL(url)
      resolve({ dataUrl: c.toDataURL('image/png'), w: c.width, h: c.height })
    }
    im.onerror = () => { URL.revokeObjectURL(url); reject(new Error('could not draw the plan')) }
    im.src = url
  })
}


/**
 * The drawing set, as a PDF of vectors.
 *
 * Not a picture of a drawing: `doc.line` and `doc.text` at paper millimetres, which is what
 * jsPDF is already opened in. So a sheet prints at true scale, a ruler on the paper agrees
 * with the dimension on it, and the file is a few kilobytes rather than a few megabytes --
 * the plan in buildReport above goes in as a rasterised SVG and this deliberately does not.
 *
 * @param {Array} sheets  from drawing.sheets()
 * @returns {Promise<Blob>}
 */
export async function buildDrawings(sheets) {
  if (!sheets?.length) throw new Error('nothing to draw')
  const first = sheets[0].size
  const doc = new jsPDF({ unit: 'mm', format: [first.w, first.h], orientation: 'landscape' })
  const hex = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]

  sheets.forEach((sheet, i) => {
    if (i) doc.addPage([sheet.size.w, sheet.size.h], 'landscape')
    // toPaper handles the elevation's flip; see drawing.js.
    const at = (p, paper) => (paper ? p : toPaper(sheet, p))
    for (const p of sheet.prims) {
      const L = LAYER[p.layer] ?? LAYER.cabinet
      const rgb = hex(L.colour)
      doc.setDrawColor(...rgb)
      doc.setLineWidth(L.width)
      if (p.k === 'line') {
        const a = at(p.a, p.paper), b = at(p.b, p.paper)
        doc.line(a.x, a.y, b.x, b.y)
      } else if (p.k === 'poly') {
        const pts = p.pts.map((q) => at(q, p.paper))
        if (p.fill) {
          doc.setFillColor(244, 241, 236)
          doc.lines(pts.slice(1).map((q, k) => [q.x - pts[k].x, q.y - pts[k].y]),
            pts[0].x, pts[0].y, [1, 1], 'F', p.close)
        }
        doc.lines(pts.slice(1).map((q, k) => [q.x - pts[k].x, q.y - pts[k].y]),
          pts[0].x, pts[0].y, [1, 1], 'S', p.close)
      } else if (p.k === 'circle') {
        const c = at(p.c, p.paper)
        doc.circle(c.x, c.y, p.paper ? p.r : p.r / sheet.scale, 'S')
      } else if (p.k === 'arc') {
        // jsPDF has no arc, so a swing is a short polyline -- twelve segments, which at the
        // size a door swing is drawn is indistinguishable from a curve.
        const r = p.paper ? p.r : p.r / sheet.scale
        const steps = 12
        const pts = []
        for (let k = 0; k <= steps; k++) {
          const a = p.from + (p.to - p.from) * (k / steps)
          pts.push(at({ x: p.c.x + Math.cos(a) * p.r, y: p.c.y + Math.sin(a) * p.r }, p.paper))
        }
        void r
        doc.lines(pts.slice(1).map((q, k) => [q.x - pts[k].x, q.y - pts[k].y]), pts[0].x, pts[0].y, [1, 1], 'S')
      } else if (p.k === 'text') {
        const q = at(p.at, p.paper)
        doc.setFont('helvetica', 'normal')
        // jsPDF sizes text in POINTS whatever the document unit is, so a paper-millimetre
        // size has to be converted. 1mm is 72/25.4 points; without this every label on
        // every sheet came out about two and a half times too big.
        doc.setFontSize(p.size * 72 / 25.4)
        doc.setTextColor(...rgb)
        doc.text(String(p.text ?? ''), q.x, q.y, {
          align: p.align === 'centre' ? 'center' : p.align === 'right' ? 'right' : 'left',
          angle: p.rot ? -p.rot : undefined,
        })
      }
    }
  })
  return doc.output('blob')
}
